import React, { useCallback, useLayoutEffect, useRef } from 'react';
import {
  ChannelList as GetStreamChannelList, ChannelListProps as DefaultChannelListProps,
} from 'stream-chat-react';
import { Channel } from 'stream-chat';
import { ChannelSearch } from '../ChannelSearch/ChannelSearch';
import { ChannelPreview } from './ChannelPreview';
import { useChatContext } from '../ChatContext';
import { useAutoCreateChatRoom } from '../hooks/useAutoCreateChatRoom';
import { ErrorSnackbar, useErrorSnackbar } from '../../notifications/ErrorSnackbar';
import { Loading } from '../../Loading';
import { EmptyStateIndicator } from './EmptyStateIndicator';
import { sortChannels } from './useChannelSort';

export type ChannelListProps = Omit<DefaultChannelListProps, 'showChannelSearch' | 'ChannelSearch' | 'Preview' | 'Filters'> & {
  chatUserId: string;
};

export const ChannelList: React.FC<ChannelListProps> = (props) => {
  const { filters: { apiFilters }, sort: { apiSort, sortBy } } = useChatContext();
  const {
    loading: isRoomChecking,
    error: roomCheckingError,
  } = useAutoCreateChatRoom(props.customActiveChannel);
  const roomErrorSnackbar = useErrorSnackbar(roomCheckingError);

  // Easy way to make links in the last message in channel preview be opened in new tab
  const channelsWrapperRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const selector = channelsWrapperRef.current;
    const onLinkClicked = (e: Event) => {
      if ((e.target as any).nodeName === 'A') {
        e.preventDefault();
        window.open((e.target as HTMLAnchorElement).href, '_blank')?.focus();
      }
    };
    selector?.addEventListener('click', onLinkClicked);
    return () => {
      selector?.removeEventListener('click', onLinkClicked);
    };
  }, []);

  const { chatUserId } = props;
  const channelRenderFilterFn = useCallback(
    (channels: Channel[]) => sortChannels(chatUserId, channels, sortBy),
    [chatUserId, sortBy],
  );

  if (isRoomChecking) {
    return <Loading center />;
  }

  return (
    <div ref={channelsWrapperRef}>
      <GetStreamChannelList
        showChannelSearch
        options={{ limit: 15, message_limit: 15 }}
        ChannelSearch={ChannelSearch}
        Preview={ChannelPreview}
        filters={apiFilters}
        sort={apiSort}
        EmptyStateIndicator={EmptyStateIndicator}
        channelRenderFilterFn={channelRenderFilterFn}
        {...props}
      />
      <ErrorSnackbar {...roomErrorSnackbar}>
        Checking room existence error
      </ErrorSnackbar>
    </div>
  );
};
